import { useState, useCallback, useRef } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { Upload, FileSpreadsheet, Check, AlertTriangle, Loader2, X, Zap, ChevronRight, RotateCcw } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const STEPS = { IDLE: 'idle', DETECTING: 'detecting', DETECTED: 'detected', IMPORTING: 'importing', DONE: 'done', ERROR: 'error' };

export default function SWMUnifiedImportWizard({ projectId, onImportComplete }) {
  const { isDark } = useTheme();
  const [step, setStep] = useState(STEPS.IDLE);
  const [file, setFile] = useState(null);
  const [detection, setDetection] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const reset = () => {
    setStep(STEPS.IDLE);
    setFile(null);
    setDetection(null);
    setImportResult(null);
    setDragOver(false);
  };

  const handleFile = useCallback(async (f) => {
    if (!f) return;
    setFile(f);
    setStep(STEPS.DETECTING);
    setDetection(null);
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append('file', f);
      const res = await api.post(`/v1/projects/${projectId}/swm/import/auto-detect`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setDetection(res.data);
      if (res.data.detected) {
        setStep(STEPS.DETECTED);
      } else {
        setStep(STEPS.ERROR);
      }
    } catch (err) {
      setDetection({ error: err.response?.data?.detail || err.message });
      setStep(STEPS.ERROR);
    }
  }, [projectId]);

  const handleImport = async () => {
    if (!file || !detection?.detected) return;
    setStep(STEPS.IMPORTING);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post(`/v1/projects/${projectId}/swm/import/auto`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(res.data);
      setStep(STEPS.DONE);
      if (res.data.status === 'COMPLETED') {
        toast.success(`Imported ${res.data.imported}/${res.data.total} records as ${res.data.detectedLabel}`);
      } else {
        toast.error(`Import failed: ${res.data.errors?.[0] || 'Unknown error'}`);
      }
      onImportComplete?.();
    } catch (err) {
      setImportResult({ status: 'FAILED', errors: [err.response?.data?.detail || err.message] });
      setStep(STEPS.DONE);
      toast.error('Import failed');
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f && (f.name.endsWith('.xlsx') || f.name.endsWith('.xls'))) {
      handleFile(f);
    } else {
      toast.error('Please drop an Excel file (.xlsx)');
    }
  };

  const confidenceColor = (c) => c >= 80 ? '#10B981' : c >= 60 ? '#FBBF24' : '#EF4444';

  return (
    <div className="rounded-xl overflow-hidden" data-testid="swm-unified-wizard"
      style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>

      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-2.5" style={{ background: 'linear-gradient(135deg, #F5920615, #F5920605)', borderBottom: '1px solid var(--border-color)' }}>
        <Zap className="w-4 h-4" style={{ color: '#F59206' }} />
        <span className="text-xs font-bold" style={{ color: 'var(--text-primary)' }}>Unified Import Wizard</span>
        <span className="text-[10px] ml-1" style={{ color: 'var(--text-muted)' }}>Drop any SWM Excel file — type auto-detected from headers</span>
        {step !== STEPS.IDLE && (
          <button onClick={reset} data-testid="swm-wizard-reset"
            className="ml-auto flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded"
            style={{ color: 'var(--text-muted)', background: 'var(--card-bg-alt)' }}>
            <RotateCcw className="w-3 h-3" /> Reset
          </button>
        )}
      </div>

      <div className="p-4">
        {/* STEP: IDLE — Drop zone */}
        {step === STEPS.IDLE && (
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => inputRef.current?.click()}
            data-testid="swm-wizard-dropzone"
            className="flex flex-col items-center justify-center gap-2 py-8 rounded-lg cursor-pointer transition-all"
            style={{
              border: `2px dashed ${dragOver ? '#F59206' : 'var(--border-color)'}`,
              background: dragOver ? '#F5920610' : 'transparent',
            }}>
            <Upload className="w-8 h-8" style={{ color: dragOver ? '#F59206' : 'var(--text-muted)', opacity: 0.6 }} />
            <p className="text-sm font-medium" style={{ color: dragOver ? '#F59206' : 'var(--text-primary)' }}>
              {dragOver ? 'Drop it here!' : 'Drag & drop any SWM Excel file'}
            </p>
            <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
              or click to browse — supports Structure Register, MTO, PO, Stock, Drawings, Mark Numbers, Mappings, GRN, Fab Progress
            </p>
            <input ref={inputRef} type="file" accept=".xlsx,.xls" className="hidden"
              onChange={e => { if (e.target.files[0]) handleFile(e.target.files[0]); e.target.value = ''; }} />
          </div>
        )}

        {/* STEP: DETECTING — Spinner */}
        {step === STEPS.DETECTING && (
          <div className="flex flex-col items-center justify-center gap-3 py-8" data-testid="swm-wizard-detecting">
            <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#F59206' }} />
            <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Analyzing headers of <strong>{file?.name}</strong>...</p>
          </div>
        )}

        {/* STEP: DETECTED — Show result + confirm */}
        {step === STEPS.DETECTED && detection && (
          <div className="space-y-3" data-testid="swm-wizard-detected">
            <div className="flex items-center gap-3">
              <FileSpreadsheet className="w-6 h-6" style={{ color: '#F59206' }} />
              <div className="flex-1">
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>File: <strong style={{ color: 'var(--text-primary)' }}>{file?.name}</strong></p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>{detection.detectedLabel}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded font-bold"
                    style={{ background: confidenceColor(detection.confidence) + '20', color: confidenceColor(detection.confidence) }}>
                    {detection.confidence}% match
                  </span>
                </div>
                <p className="text-[10px] mt-0.5" style={{ color: 'var(--text-muted)' }}>
                  {detection.requiredMatched}/{detection.requiredTotal} required columns, {detection.columnsMatched}/{detection.columnsTotal} total columns matched
                </p>
              </div>
            </div>

            {/* Runner-up */}
            {detection.runnerUp && detection.runnerUp.score > 30 && (
              <p className="text-[10px] px-3 py-1.5 rounded" style={{ background: 'var(--card-bg-alt)', color: 'var(--text-muted)' }}>
                Runner-up: {detection.runnerUp.label} ({detection.runnerUp.score}% match)
              </p>
            )}

            {/* Action buttons */}
            <div className="flex items-center gap-2 pt-1">
              <button onClick={handleImport} data-testid="swm-wizard-confirm-import"
                className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold rounded-lg text-white transition-colors"
                style={{ background: '#F59206' }}>
                <Check className="w-3.5 h-3.5" /> Import as {detection.detectedLabel}
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
              <button onClick={reset} data-testid="swm-wizard-cancel"
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg transition-colors"
                style={{ color: 'var(--text-muted)', background: 'var(--card-bg-alt)' }}>
                <X className="w-3.5 h-3.5" /> Cancel
              </button>
            </div>
          </div>
        )}

        {/* STEP: IMPORTING */}
        {step === STEPS.IMPORTING && (
          <div className="flex flex-col items-center justify-center gap-3 py-8" data-testid="swm-wizard-importing">
            <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#F59206' }} />
            <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
              Importing <strong>{file?.name}</strong> as <strong>{detection?.detectedLabel}</strong>...
            </p>
          </div>
        )}

        {/* STEP: DONE */}
        {step === STEPS.DONE && importResult && (
          <div className="space-y-3" data-testid="swm-wizard-done">
            <div className="flex items-center gap-3">
              {importResult.status === 'COMPLETED'
                ? <div className="p-2 rounded-full" style={{ background: '#10B98120' }}><Check className="w-5 h-5" style={{ color: '#10B981' }} /></div>
                : <div className="p-2 rounded-full" style={{ background: '#EF444420' }}><AlertTriangle className="w-5 h-5" style={{ color: '#EF4444' }} /></div>
              }
              <div>
                <p className="text-sm font-bold" style={{ color: importResult.status === 'COMPLETED' ? '#10B981' : '#EF4444' }}>
                  {importResult.status === 'COMPLETED' ? 'Import Successful' : 'Import Failed'}
                </p>
                <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
                  {importResult.detectedLabel} — {importResult.imported}/{importResult.total} records imported
                  {importResult.confidence && <> (detected at {importResult.confidence}% confidence)</>}
                </p>
              </div>
            </div>
            {importResult.errors?.length > 0 && (
              <div className="max-h-24 overflow-y-auto rounded p-2 text-[10px] space-y-0.5"
                style={{ background: '#EF444408', color: '#EF4444', border: '1px solid #EF444420' }}>
                {importResult.errors.slice(0, 10).map((e, i) => <div key={i}>{e}</div>)}
                {importResult.errors.length > 10 && <div>...and {importResult.errors.length - 10} more</div>}
              </div>
            )}
            <button onClick={reset} data-testid="swm-wizard-import-another"
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors"
              style={{ color: '#F59206', background: '#F5920610' }}>
              <Upload className="w-3.5 h-3.5" /> Import Another File
            </button>
          </div>
        )}

        {/* STEP: ERROR */}
        {step === STEPS.ERROR && detection && (
          <div className="space-y-3" data-testid="swm-wizard-error">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full" style={{ background: '#EF444420' }}>
                <AlertTriangle className="w-5 h-5" style={{ color: '#EF4444' }} />
              </div>
              <div>
                <p className="text-sm font-bold" style={{ color: '#EF4444' }}>Could Not Detect Import Type</p>
                <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{detection.error}</p>
              </div>
            </div>
            {detection.scores && Object.keys(detection.scores).length > 0 && (
              <div className="rounded p-2 text-[10px] space-y-1" style={{ background: 'var(--card-bg-alt)' }}>
                <p className="font-medium" style={{ color: 'var(--text-muted)' }}>Closest matches:</p>
                {Object.entries(detection.scores).map(([k, v]) => (
                  <div key={k} className="flex items-center gap-2">
                    <span style={{ color: 'var(--text-primary)' }}>{v.label}</span>
                    <span style={{ color: confidenceColor(v.score) }}>{v.score}%</span>
                  </div>
                ))}
              </div>
            )}
            {detection.fileHeaders?.length > 0 && (
              <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
                Headers found: {detection.fileHeaders.join(', ')}
              </p>
            )}
            <button onClick={reset} data-testid="swm-wizard-try-again"
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg"
              style={{ color: '#F59206', background: '#F5920610' }}>
              <RotateCcw className="w-3.5 h-3.5" /> Try Another File
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function confidenceColor(c) {
  return c >= 80 ? '#10B981' : c >= 60 ? '#FBBF24' : '#EF4444';
}
